import { MouseEvent, useState } from "react";
import * as React from "react";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Menu from "@mui/material/Menu";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  selectCurrentUser,
  selectHasRegions,
  selectHasServiceLocations,
  setCurrentClinic,
  setCurrentRegion
} from "features/auth/auth-slice";
import { useLazyGetClinicQuery } from "services/clinics";
import { ClinicsResponse } from "services/types/clinics.type";
import { Region } from "services/types/regions.type";

import RegionsSelectorHeader from "./regions-selector-header.component";
import RegionsSelectorItem from "./regions-selector-item.component";

type CurrentRegionSelectorProps = {
  minimalView: boolean;
  regions: Region[];
  clinics: ClinicsResponse;
};

const RegionsSelector: React.FC<CurrentRegionSelectorProps> = ({
  minimalView,
  regions,
  clinics
}) => {
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector(selectCurrentUser);
  const hasRegions = useAppSelector(selectHasRegions);
  const hasServiceLocations = useAppSelector(selectHasServiceLocations);
  const [getClinicDetails] = useLazyGetClinicQuery();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedRegion, setExpandedRegion] = useState<number | null>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget.parentElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRegionSelect = (region: Region) => {
    const clinicId = region.clinics?.[0]?.id ?? currentUser.default_clinic?.id;
    dispatch(setCurrentRegion(region));
    if (clinicId) {
      handleClinicSelect(clinicId);
    }
  };

  const handleClinicSelect = (clinicId: number) => {
    const clinicData = clinics.find((clinic) => clinic.id === clinicId);

    // First, immediately set the clinic with minimal details
    dispatch(
      setCurrentClinic({
        currentClinic: { ...clinicData },
        numberOfClinics: clinics.length,
        hasRegions,
        hasServiceLocations
      })
    );

    // Then fetch and update with full clinic details
    if (clinicData?.id) {
      const selectedClinicId = clinicData.id;
      getClinicDetails({ clinicId: selectedClinicId })
        .then((result) => {
          if (result.data) {
            // Only update if this is still the currently selected clinic
            dispatch((dispatch, getState) => {
              const currentClinicId = getState().auth.currentClinic?.id;
              if (currentClinicId === selectedClinicId) {
                dispatch(
                  setCurrentClinic({
                    currentClinic: result.data,
                    numberOfClinics: clinics.length,
                    hasRegions,
                    hasServiceLocations
                  })
                );
              }
            });
          }
        })
        .catch((error) => {
          console.error("Failed to fetch full clinic details:", error);
        });
    }
  };

  return (
    <Box>
      <RegionsSelectorHeader
        minimalView={minimalView}
        regionsLength={regions.length}
        handleClick={handleClick}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{
          sx: { maxHeight: 600, minWidth: "20rem", zIndex: 2000 }
        }}
        sx={{ zIndex: 2000 }}
      >
        <List component="div" disablePadding>
          {regions?.map((region) => {
            return (
              <RegionsSelectorItem
                key={region.id}
                clinics={clinics}
                region={region}
                expandedRegion={expandedRegion}
                onRegionSelect={handleRegionSelect}
                onClinicSelect={handleClinicSelect}
                onToggleRegionExpand={(event) => {
                  event.stopPropagation();
                  setExpandedRegion((prevRegion) =>
                    prevRegion === region.id ? null : region.id
                  );
                }}
              />
            );
          })}
        </List>
      </Menu>
    </Box>
  );
};

export default RegionsSelector;
